import { Box, styled } from "@mui/material";
import { Clear } from "@procore/core-icons";
import { Button, Search, Spinner, Typography } from "@procore/core-react";
import { vec2 } from "gl-matrix";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { TOOLBAR_Z_INDEX } from "components/toolbars";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { CameraType, renderActions } from "features/render";
import { useOverlayZIndex } from "hooks/useOverlayZIndex";
import { telemetry } from "telemetry";
import { AsyncStatus } from "types/misc";

import { ControlBarWeb } from "./ControlBarWeb";
import { LevelsTreeWeb } from "./levelsTreeWeb";
import {
    selectCurrentDrawing,
    selectMiniMapDrawingIds,
    selectTempDrawing,
    selectTempLevel,
    setCurrentDrawing,
    setDrawingData,
    setTempDrawingId,
} from "./minimapSlice";
import {
    calculateImageFitBox,
    drawingToModelSpace,
    drawLargeMapPositionIndicator,
    EYE_ELEVATION_OFFSET,
    fetchDrawingData,
    modelToDrawingSpace,
} from "./miniMapUtils";
import {
    createInitialNavigationState,
    ENTRANCE_OPACITY_DURATION,
    ENTRANCE_SCALE_DURATION,
    ENTRANCE_TOTAL_DURATION,
    entranceOpacityAt,
    entranceScaleAt,
    HINT_DELAY,
    INDICATOR_PROXIMITY,
    type NavigationState,
    PAN_THRESHOLD,
    ROTATION_DEAD_ZONE,
} from "./navigationAnimations";
import { NavigationTooltip, PulseRing, RotationHints } from "./navigationOverlay";
import type { DrawingSpacePosition } from "./types";

const IMAGE_MARGIN = Object.freeze({
    top: 24,
    right: 24,
    bottom: 24 + 50 + 24,
    left: 24,
});

const Overlay = styled(Box)<{ open: boolean }>(({ open }) => ({
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    opacity: open ? 1 : 0,
    visibility: open ? "visible" : "hidden",
    transition: "opacity 300ms ease-out, visibility 300ms ease-out",
    pointerEvents: open ? "auto" : "none",
}));

const TearSheet = styled(Box)<{ open: boolean }>(({ open }) => ({
    position: "fixed",
    top: "68px",
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#fff",
    boxShadow: "0px -4px 20px rgba(0, 0, 0, 0.15)",
    transform: open ? "translateY(0)" : "translateY(100%)",
    transition: "transform 400ms cubic-bezier(0.4, 0, 0.2, 1)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    pointerEvents: "auto",
}));

const TearSheetContent = styled(Box)({
    display: "flex",
    height: "100%",
    width: "100%",
    overflow: "hidden",
    position: "relative",
});

const LeftPanel = styled(Box)({
    width: "25%",
    borderRight: "1px solid rgba(214, 218, 220, 1)",
    boxShadow: "4px 0px 12px 0px rgba(0, 0, 0, 0.2)",
    backgroundColor: "rgba(255, 255, 255, 1)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    minWidth: 300,
});

const RightPanel = styled(Box)({
    width: "75%",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fafafa",
    overflow: "hidden",
    minWidth: 300,
});

const LoadingOverlay = styled(Box)({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
    gap: 16,
});

interface LargeMapWebProps {
    open: boolean;
    onClose: () => void;
}

function isInsideFitBox(
    mouseX: number,
    mouseY: number,
    fitBox: { x: number; y: number; width: number; height: number },
): boolean {
    return (
        mouseX >= fitBox.x &&
        mouseX <= fitBox.x + fitBox.width &&
        mouseY >= fitBox.y &&
        mouseY <= fitBox.y + fitBox.height
    );
}

function getIndicatorCanvasPos(
    placedPos: DrawingSpacePosition,
    fitBox: { x: number; y: number; width: number; height: number },
): { x: number; y: number } {
    return {
        x: fitBox.x + placedPos.position[0] * fitBox.width,
        y: fitBox.y + placedPos.position[1] * fitBox.height,
    };
}

export function LargeMapWeb({ open, onClose }: LargeMapWebProps) {
    const largeCanvasRef = useRef<HTMLCanvasElement>(null);
    const rightPanelRef = useRef<HTMLDivElement>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [canvasSize, setCanvasSize] = useState([800, 600]);
    const [placedPosition, setPlacedPosition] = useState<DrawingSpacePosition | null>(null);
    const placedPositionRef = useRef<DrawingSpacePosition | null>(null);
    placedPositionRef.current = placedPosition;
    const imageFitBoxRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);
    const [scale, setScale] = useState(1);
    const [panOffset, setPanOffset] = useState<vec2>(vec2.fromValues(0, 0));
    const isPanningRef = useRef(false);
    const lastPanPosRef = useRef<vec2>(vec2.fromValues(0, 0));
    const mousedownOriginRef = useRef<{ x: number; y: number } | null>(null);
    const mouseButtonRef = useRef(-1);

    const navRef = useRef<NavigationState>(createInitialNavigationState());
    const [showPulse, setShowPulse] = useState(false);
    const [pulsePos, setPulsePos] = useState({ x: 0, y: 0 });
    const [hintsMounted, setHintsMounted] = useState(false);
    const [hintsExiting, setHintsExiting] = useState(false);
    const [hintsPos, setHintsPos] = useState({ x: 0, y: 0 });
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [tooltipText, setTooltipText] = useState("");
    const [tooltipMouse, setTooltipMouse] = useState({ x: 0, y: 0 });

    const drawingIds = useAppSelector(selectMiniMapDrawingIds);
    const currentDrawing = useAppSelector(selectCurrentDrawing);
    const tempDrawing = useAppSelector(selectTempDrawing);
    const tempLevel = useAppSelector(selectTempLevel);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const {
        state: { view },
    } = useExplorerGlobals();

    useEffect(() => {
        if (open) {
            dispatch(setTempDrawingId(currentDrawing?.id ?? null));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, dispatch]);

    const resetNavState = useCallback(() => {
        const nav = navRef.current;
        if (nav.animFrameId !== null) {
            cancelAnimationFrame(nav.animFrameId);
        }
        if (nav.hintTimerId !== null) {
            clearTimeout(nav.hintTimerId);
        }
        Object.assign(navRef.current, createInitialNavigationState());
        setShowPulse(false);
        setHintsMounted(false);
        setHintsExiting(false);
        setTooltipVisible(false);
    }, []);

    const handleClose = useCallback(() => {
        resetNavState();
        setPlacedPosition(null);
        setScale(1);
        setPanOffset(vec2.fromValues(0, 0));
        dispatch(setTempDrawingId(null));
        onClose();
    }, [onClose, dispatch, resetNavState]);

    const totalPages = drawingIds.length;
    const currentPageIndex = tempDrawing ? drawingIds.indexOf(tempDrawing.id) : -1;

    const handlePrevPage = useCallback(() => {
        if (totalPages === 0) {
            return;
        }
        const prevIndex = currentPageIndex <= 0 ? totalPages - 1 : currentPageIndex - 1;
        dispatch(setTempDrawingId(drawingIds[prevIndex]));
        setScale(1);
        setPanOffset(vec2.fromValues(0, 0));
        resetNavState();
        setPlacedPosition(null);
    }, [currentPageIndex, totalPages, drawingIds, dispatch, resetNavState]);

    const handleNextPage = useCallback(() => {
        if (totalPages === 0) {
            return;
        }
        const nextIndex = currentPageIndex >= totalPages - 1 ? 0 : currentPageIndex + 1;
        dispatch(setTempDrawingId(drawingIds[nextIndex]));
        setScale(1);
        setPanOffset(vec2.fromValues(0, 0));
        resetNavState();
        setPlacedPosition(null);
    }, [currentPageIndex, totalPages, drawingIds, dispatch, resetNavState]);

    const handleZoomIn = useCallback(() => {
        const zoomFactor = 1.1;
        setScale((prev) => Math.min(10, prev * zoomFactor));
        setPanOffset((prev) => vec2.fromValues(prev[0] * zoomFactor, prev[1] * zoomFactor));
    }, []);

    const handleZoomOut = useCallback(() => {
        const zoomFactor = 1 / 1.1;
        setScale((prev) => Math.max(0.1, prev * zoomFactor));
        setPanOffset((prev) => vec2.fromValues(prev[0] * zoomFactor, prev[1] * zoomFactor));
    }, []);

    const handleZoomToValue = useCallback((newScale: number) => {
        setScale((prevScale) => {
            const zoomFactor = newScale / prevScale;
            setPanOffset((prev) => vec2.fromValues(prev[0] * zoomFactor, prev[1] * zoomFactor));
            return newScale;
        });
    }, []);

    useEffect(() => {
        if (!open) {
            return;
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && navRef.current.isTrackingRotation) {
                e.preventDefault();
                e.stopPropagation();
                resetNavState();
                setPlacedPosition(null);
            } else if (e.key === "ArrowLeft") {
                e.preventDefault();
                e.stopPropagation();
                handlePrevPage();
            } else if (e.key === "ArrowRight") {
                e.preventDefault();
                e.stopPropagation();
                handleNextPage();
            }
        };

        window.addEventListener("keydown", handleKeyDown, { capture: true });
        return () => window.removeEventListener("keydown", handleKeyDown, { capture: true });
    }, [open, handlePrevPage, handleNextPage, resetNavState]);

    useEffect(() => {
        if (!open || !rightPanelRef.current) {
            return;
        }

        const updateCanvasSize = () => {
            const panel = rightPanelRef.current;
            if (panel) {
                const rect = panel.getBoundingClientRect();
                const computedStyle = window.getComputedStyle(panel);
                const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
                const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
                const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
                const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
                const availableWidth = Math.max(1, rect.width - (paddingLeft + paddingRight));
                const availableHeight = Math.max(1, rect.height - (paddingTop + paddingBottom));
                setCanvasSize([availableWidth, availableHeight]);
            }
        };

        updateCanvasSize();
        const resizeObserver = new ResizeObserver(updateCanvasSize);
        resizeObserver.observe(rightPanelRef.current);

        return () => resizeObserver.disconnect();
    }, [open]);

    // ─── Entrance animation via requestAnimationFrame ───

    const requestRedraw = useCallback(() => {
        const canvas = largeCanvasRef.current;
        if (!canvas) {
            return;
        }
        const ctx = canvas.getContext("2d");
        if (
            !ctx ||
            !tempDrawing?.drawingData.asyncState ||
            tempDrawing.drawingData.asyncState.status !== AsyncStatus.Success
        ) {
            return;
        }
        const img = tempDrawing.drawingData.asyncState.data;
        const [cw, ch] = canvasSize;

        const dpr = window.devicePixelRatio || 1;
        canvas.width = cw * dpr;
        canvas.height = ch * dpr;
        canvas.style.width = `${cw}px`;
        canvas.style.height = `${ch}px`;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        ctx.clearRect(0, 0, cw, ch);

        const fitBox = calculateImageFitBox({
            imageDimensions: [img.width, img.height],
            canvasDimensions: [cw, ch],
            scale,
            panOffset,
            margin: IMAGE_MARGIN,
        });
        imageFitBoxRef.current = fitBox;
        const { width, height, x, y } = fitBox;

        ctx.save();
        ctx.shadowColor = "rgba(0, 0, 0, 0.2)";
        ctx.shadowBlur = 20;
        ctx.fillStyle = "white";
        ctx.fillRect(x, y, width, height);
        ctx.restore();

        ctx.drawImage(img, x, y, width, height);

        const nav = navRef.current;
        const placed = placedPositionRef.current;

        if (placed) {
            const posX = x + placed.position[0] * width;
            const posY = y + placed.position[1] * height;

            let iScale = 1;
            let iOpacity = 1;

            if (nav.entrancePhase === "hidden") {
                iScale = 4;
                iOpacity = 0;
            } else if (nav.entrancePhase === "entering") {
                const elapsed = performance.now() - nav.entranceStartTime;
                const scaleProgress = Math.min(1, elapsed / ENTRANCE_SCALE_DURATION);
                const opacityProgress = Math.min(1, elapsed / ENTRANCE_OPACITY_DURATION);
                iScale = entranceScaleAt(scaleProgress);
                iOpacity = entranceOpacityAt(opacityProgress);
            }

            drawLargeMapPositionIndicator(ctx, posX, posY, placed.rotation, iScale, iOpacity);
        } else if (view && tempDrawing?.modelAB && tempDrawing?.drawingAB) {
            const camera = view.renderState.camera;
            const position = modelToDrawingSpace(tempDrawing.modelAB, tempDrawing.drawingAB, camera);
            const posX = x + position.position[0] * width;
            const posY = y + position.position[1] * height;
            drawLargeMapPositionIndicator(ctx, posX, posY, position.rotation);
        }
    }, [canvasSize, scale, panOffset, tempDrawing, view]);

    const startEntranceAnimation = useCallback(
        (canvasX: number, canvasY: number) => {
            const nav = navRef.current;
            nav.entrancePhase = "hidden";
            nav.showPulse = true;
            nav.isTrackingRotation = true;
            nav.hasRotated = false;
            nav.tooltipText = "confirm";
            nav.tooltipVisible = false;

            setTooltipVisible(false);
            setShowPulse(true);
            setPulsePos({ x: canvasX, y: canvasY });

            requestRedraw();

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    nav.entrancePhase = "entering";
                    nav.entranceStartTime = performance.now();

                    const animate = () => {
                        const elapsed = performance.now() - nav.entranceStartTime;
                        requestRedraw();

                        if (elapsed < ENTRANCE_TOTAL_DURATION) {
                            nav.animFrameId = requestAnimationFrame(animate);
                        } else {
                            nav.entrancePhase = "placed";
                            nav.animFrameId = null;
                            requestRedraw();
                        }
                    };
                    nav.animFrameId = requestAnimationFrame(animate);
                });
            });

            nav.hintTimerId = setTimeout(() => {
                nav.hintTimerId = null;
                if (!nav.hasRotated && nav.isTrackingRotation) {
                    nav.hintsCanShow = true;
                    nav.hintsMounted = true;
                    const fitBox = imageFitBoxRef.current;
                    const currentPlaced = placedPositionRef.current;
                    if (fitBox && currentPlaced) {
                        setHintsPos(getIndicatorCanvasPos(currentPlaced, fitBox));
                    } else {
                        setHintsPos({ x: canvasX, y: canvasY });
                    }
                    setHintsMounted(true);
                    setHintsExiting(false);
                }
            }, HINT_DELAY);
        },
        [requestRedraw],
    );

    // ─── Mouse handlers ───

    const calculateRotationToMouse = useCallback((indicatorPos: vec2, mouseX: number, mouseY: number): number => {
        const fitBox = imageFitBoxRef.current;
        if (!fitBox) {
            return 0;
        }
        const indicatorCanvasX = fitBox.x + indicatorPos[0] * fitBox.width;
        const indicatorCanvasY = fitBox.y + indicatorPos[1] * fitBox.height;
        const dx = mouseX - indicatorCanvasX;
        const dy = mouseY - indicatorCanvasY;
        const angleRad = Math.atan2(dx, -dy);
        let angleDeg = angleRad * (180 / Math.PI);
        if (angleDeg < 0) {
            angleDeg += 360;
        }
        return angleDeg;
    }, []);

    const handleCanvasMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = largeCanvasRef.current;
        if (!canvas) {
            return;
        }
        e.preventDefault();
        mouseButtonRef.current = e.button;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        mousedownOriginRef.current = { x, y };
        isPanningRef.current = false;
        lastPanPosRef.current = vec2.fromValues(x, y);
    }, []);

    const handleCanvasMouseMove = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            const canvas = largeCanvasRef.current;
            if (!canvas) {
                return;
            }

            const rect = canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            const fitBox = imageFitBoxRef.current;
            const nav = navRef.current;
            const onDrawing = fitBox ? isInsideFitBox(mouseX, mouseY, fitBox) : false;

            // Tooltip position update
            setTooltipMouse({ x: mouseX, y: mouseY });

            // Tooltip visibility: hide near indicator, when panning, or off drawing
            let shouldShowTooltip = onDrawing && !nav.mapPanning;
            if (shouldShowTooltip && placedPosition && fitBox) {
                const indPos = getIndicatorCanvasPos(placedPosition, fitBox);
                const dist = Math.hypot(mouseX - indPos.x, mouseY - indPos.y);
                if (dist < INDICATOR_PROXIMITY) {
                    shouldShowTooltip = false;
                }
            }
            setTooltipVisible(shouldShowTooltip);
            setTooltipText(nav.isTrackingRotation ? t("clickToConfirmDirection") : t("clickToNavigateHere"));

            // Panning detection
            const origin = mousedownOriginRef.current;
            if (origin && (e.buttons & 1 || mouseButtonRef.current === 1)) {
                const dx = mouseX - origin.x;
                const dy = mouseY - origin.y;
                const dist = Math.hypot(dx, dy);

                if (!isPanningRef.current && dist > PAN_THRESHOLD) {
                    isPanningRef.current = true;
                    nav.mapPanning = true;
                    canvas.style.cursor = "grabbing";
                    setTooltipVisible(false);
                }

                if (isPanningRef.current) {
                    const pdx = mouseX - lastPanPosRef.current[0];
                    const pdy = mouseY - lastPanPosRef.current[1];
                    setPanOffset((prev) => vec2.fromValues(prev[0] + pdx, prev[1] + pdy));
                    lastPanPosRef.current = vec2.fromValues(mouseX, mouseY);
                    return;
                }
            }

            // Cursor (when not panning)
            if (!isPanningRef.current) {
                canvas.style.cursor = onDrawing ? "crosshair" : "default";
            }

            // Rotation tracking
            if (nav.isTrackingRotation && placedPosition) {
                const clickOrig = nav.clickOrigin;
                if (clickOrig && !nav.hasRotated) {
                    const rdx = mouseX - clickOrig.x;
                    const rdy = mouseY - clickOrig.y;
                    if (Math.hypot(rdx, rdy) < ROTATION_DEAD_ZONE) {
                        return;
                    }
                    nav.hasRotated = true;

                    if (nav.hintTimerId !== null) {
                        clearTimeout(nav.hintTimerId);
                        nav.hintTimerId = null;
                    }
                    if (nav.hintsMounted && !nav.hintsExiting) {
                        nav.hintsExiting = true;
                        setHintsExiting(true);
                    }
                }

                const rotation = calculateRotationToMouse(placedPosition.position, mouseX, mouseY);
                setPlacedPosition((prev) => (prev ? { ...prev, rotation } : null));
            }
        },
        [placedPosition, calculateRotationToMouse, t],
    );

    const handleCanvasMouseUp = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            const canvas = largeCanvasRef.current;
            const fitBox = imageFitBoxRef.current;
            const origin = mousedownOriginRef.current;
            const nav = navRef.current;

            const wasPanning = isPanningRef.current;
            isPanningRef.current = false;
            nav.mapPanning = false;
            mousedownOriginRef.current = null;
            mouseButtonRef.current = -1;

            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const mouseX = e.clientX - rect.left;
                const mouseY = e.clientY - rect.top;
                const onDrawing = fitBox ? isInsideFitBox(mouseX, mouseY, fitBox) : false;
                canvas.style.cursor = onDrawing ? "crosshair" : "default";
            }

            if (wasPanning || !origin) {
                return;
            }

            if (!canvas || !fitBox || !tempDrawing?.modelAB || !tempDrawing?.drawingAB) {
                return;
            }

            const rect = canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            const dist = Math.hypot(mouseX - origin.x, mouseY - origin.y);
            if (dist > PAN_THRESHOLD) {
                return;
            }

            if (!isInsideFitBox(mouseX, mouseY, fitBox)) {
                return;
            }

            if (!placedPosition) {
                const percentX = (mouseX - fitBox.x) / fitBox.width;
                const percentY = (mouseY - fitBox.y) / fitBox.height;
                const newPos: DrawingSpacePosition = {
                    position: vec2.fromValues(percentX, percentY),
                    rotation: 0,
                };
                setPlacedPosition(newPos);

                nav.clickOrigin = { x: mouseX, y: mouseY };
                const canvasX = fitBox.x + percentX * fitBox.width;
                const canvasY = fitBox.y + percentY * fitBox.height;

                requestAnimationFrame(() => startEntranceAnimation(canvasX, canvasY));
            } else if (view && tempLevel) {
                const elevation = tempLevel.elevation + EYE_ELEVATION_OFFSET;
                const modelSpacePos = drawingToModelSpace(
                    placedPosition,
                    tempDrawing.modelAB,
                    tempDrawing.drawingAB,
                    elevation,
                );

                dispatch(
                    renderActions.setCamera({
                        type: CameraType.Pinhole,
                        goTo: {
                            position: modelSpacePos.position,
                            rotation: modelSpacePos.rotation,
                            flyTime: 1000,
                        },
                    }),
                );

                dispatch(setCurrentDrawing(tempDrawing.id));
                handleClose();
            }
        },
        [placedPosition, tempDrawing, tempLevel, view, handleClose, dispatch, startEntranceAnimation],
    );

    const handleCanvasMouseLeave = useCallback(() => {
        isPanningRef.current = false;
        navRef.current.mapPanning = false;
        mousedownOriginRef.current = null;
        mouseButtonRef.current = -1;
        setTooltipVisible(false);
    }, []);

    // Wheel zoom
    useEffect(() => {
        const canvas = largeCanvasRef.current;
        if (!canvas || !open) {
            return;
        }

        const handleWheel = (e: WheelEvent) => {
            e.preventDefault();
            const fitBox = imageFitBoxRef.current;
            if (!fitBox) {
                return;
            }
            const rect = canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
            const newScale = Math.max(0.1, Math.min(10, scale * zoomFactor));

            const imageCenterX = fitBox.x + fitBox.width / 2;
            const imageCenterY = fitBox.y + fitBox.height / 2;
            const mouseOffsetX = mouseX - imageCenterX;
            const mouseOffsetY = mouseY - imageCenterY;

            const scaleRatio = newScale / scale;
            const newPanOffsetX = panOffset[0] - mouseOffsetX * (scaleRatio - 1);
            const newPanOffsetY = panOffset[1] - mouseOffsetY * (scaleRatio - 1);

            setScale(newScale);
            setPanOffset(vec2.fromValues(newPanOffsetX, newPanOffsetY));
        };

        canvas.addEventListener("wheel", handleWheel, { passive: false });
        return () => canvas.removeEventListener("wheel", handleWheel);
    }, [open, scale, panOffset]);

    // Canvas redraw on state changes
    useEffect(() => {
        if (!open) {
            return;
        }
        const canvas = largeCanvasRef.current;
        if (!canvas) {
            return;
        }
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            return;
        }

        const dpr = window.devicePixelRatio || 1;
        const [canvasWidth, canvasHeight] = canvasSize;

        canvas.width = canvasWidth * dpr;
        canvas.height = canvasHeight * dpr;
        canvas.style.width = `${canvasWidth}px`;
        canvas.style.height = `${canvasHeight}px`;
        ctx.scale(dpr, dpr);

        if (!tempDrawing) {
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);
            return;
        }

        const { status } = tempDrawing.drawingData.asyncState;
        switch (status) {
            case AsyncStatus.Initial:
                dispatch(setDrawingData({ drawingId: tempDrawing.id, asyncState: { status: AsyncStatus.Loading } }));
                fetchDrawingData(tempDrawing.drawingData.url)
                    .then((imgBitmap) => {
                        dispatch(
                            setDrawingData({
                                drawingId: tempDrawing.id,
                                asyncState: { status: AsyncStatus.Success, data: imgBitmap },
                            }),
                        );
                    })
                    .catch((error) => {
                        const e = error as Error;
                        telemetry.trackCaughtException({
                            source: "miniMap.largeMapWeb.fetchDrawingData",
                            category: "render",
                            error,
                            extraAttributes: { drawingId: tempDrawing.id },
                        });
                        dispatch(
                            setDrawingData({
                                drawingId: tempDrawing.id,
                                asyncState: { status: AsyncStatus.Error, msg: e.message },
                            }),
                        );
                    });
                ctx.clearRect(0, 0, canvasWidth, canvasHeight);
                break;
            case AsyncStatus.Loading:
                ctx.clearRect(0, 0, canvasWidth, canvasHeight);
                break;
            case AsyncStatus.Success: {
                const img = tempDrawing.drawingData.asyncState.data;
                ctx.clearRect(0, 0, canvasWidth, canvasHeight);

                const fitBox = calculateImageFitBox({
                    imageDimensions: [img.width, img.height],
                    canvasDimensions: [canvasWidth, canvasHeight],
                    scale,
                    panOffset,
                    margin: IMAGE_MARGIN,
                });
                imageFitBoxRef.current = fitBox;
                const { width, height, x, y } = fitBox;

                ctx.save();
                ctx.shadowColor = "rgba(0, 0, 0, 0.2)";
                ctx.shadowBlur = 20;
                ctx.fillStyle = "white";
                ctx.fillRect(x, y, width, height);
                ctx.restore();

                ctx.drawImage(img, x, y, width, height);

                const nav = navRef.current;
                if (placedPosition) {
                    const posX = x + placedPosition.position[0] * width;
                    const posY = y + placedPosition.position[1] * height;

                    let iScale = 1;
                    let iOpacity = 1;
                    if (nav.entrancePhase === "hidden") {
                        iScale = 4;
                        iOpacity = 0;
                    } else if (nav.entrancePhase === "entering") {
                        const elapsed = performance.now() - nav.entranceStartTime;
                        iScale = entranceScaleAt(Math.min(1, elapsed / ENTRANCE_SCALE_DURATION));
                        iOpacity = entranceOpacityAt(Math.min(1, elapsed / ENTRANCE_OPACITY_DURATION));
                    }
                    drawLargeMapPositionIndicator(ctx, posX, posY, placedPosition.rotation, iScale, iOpacity);
                } else if (view && tempDrawing?.modelAB && tempDrawing?.drawingAB) {
                    const camera = view.renderState.camera;
                    const position = modelToDrawingSpace(tempDrawing.modelAB, tempDrawing.drawingAB, camera);
                    const posX = x + position.position[0] * width;
                    const posY = y + position.position[1] * height;
                    drawLargeMapPositionIndicator(ctx, posX, posY, position.rotation);
                }
                break;
            }
            case AsyncStatus.Error:
                ctx.clearRect(0, 0, canvasWidth, canvasHeight);
                break;
            default:
        }
    }, [open, tempDrawing, canvasSize, placedPosition, scale, panOffset, dispatch, view]);

    const overlayZIndex = useOverlayZIndex();
    const zIndex = Math.max(overlayZIndex, TOOLBAR_Z_INDEX.LARGE_MAP_WEB);

    return createPortal(
        <div
            style={{
                position: "fixed",
                inset: 0,
                pointerEvents: "none",
                zIndex,
            }}
        >
            <Overlay open={open} onClick={handleClose} />
            <TearSheet open={open}>
                <TearSheetContent>
                    <LeftPanel>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                                borderBottom: "1px solid rgba(214, 218, 220, 1)",
                                padding: "16px 16px 8px 16px",
                                flexShrink: 0,
                            }}
                        >
                            <Typography intent="h3">{t("modelLevelsHeader")}</Typography>
                            <Typography intent="body" color={"gray45"} style={{ marginBottom: "16px" }}>
                                {t("modelLevelsHeaderSubtext")}
                            </Typography>
                            <Search
                                placeholder="Filter by Keyword"
                                color={"gray94"}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    gap: "8px",
                                    paddingTop: "8px",
                                    paddingRight: "16px",
                                    paddingBottom: "8px",
                                    paddingLeft: "16px",
                                }}
                            />
                        </Box>
                        <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                            <LevelsTreeWeb
                                searchTerm={searchTerm}
                                onDrawingChange={() => {
                                    setScale(1);
                                    setPanOffset(vec2.fromValues(0, 0));
                                    resetNavState();
                                    setPlacedPosition(null);
                                }}
                                onClearSearch={() => setSearchTerm("")}
                            />
                        </Box>
                    </LeftPanel>
                    <RightPanel ref={rightPanelRef}>
                        {tempDrawing && (
                            <canvas
                                ref={largeCanvasRef}
                                onMouseMove={handleCanvasMouseMove}
                                onMouseDown={handleCanvasMouseDown}
                                onMouseUp={handleCanvasMouseUp}
                                onMouseLeave={handleCanvasMouseLeave}
                                onContextMenu={(e) => e.preventDefault()}
                            />
                        )}
                        <NavigationTooltip
                            visible={tooltipVisible}
                            text={tooltipText}
                            mouseX={tooltipMouse.x}
                            mouseY={tooltipMouse.y}
                        />
                        {showPulse && (
                            <PulseRing x={pulsePos.x} y={pulsePos.y} onComplete={() => setShowPulse(false)} />
                        )}
                        {hintsMounted && (
                            <RotationHints
                                x={hintsPos.x}
                                y={hintsPos.y}
                                exiting={hintsExiting}
                                onExitComplete={() => {
                                    setHintsMounted(false);
                                    setHintsExiting(false);
                                }}
                            />
                        )}
                        {tempDrawing &&
                            (tempDrawing.drawingData.asyncState.status === AsyncStatus.Loading ||
                                tempDrawing.drawingData.asyncState.status === AsyncStatus.Initial) && (
                                <LoadingOverlay>
                                    <Spinner size="lg" loading={true} />
                                    <Typography>{t("loadingDrawing")}</Typography>
                                </LoadingOverlay>
                            )}
                        <ControlBarWeb
                            currentPageIndex={currentPageIndex}
                            totalPages={totalPages}
                            scale={scale}
                            onPrevPage={handlePrevPage}
                            onNextPage={handleNextPage}
                            onZoomIn={handleZoomIn}
                            onZoomOut={handleZoomOut}
                            onZoomToValue={handleZoomToValue}
                        />
                    </RightPanel>
                </TearSheetContent>
            </TearSheet>
            <Button
                onClick={handleClose}
                variant="secondary"
                style={{
                    position: "fixed",
                    top: 16,
                    right: 24,
                    minWidth: 36,
                    width: 36,
                    height: 36,
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: open ? "translateY(0)" : "translateY(100vh)",
                    transition: "transform 400ms cubic-bezier(0.4, 0, 0.2, 1)",
                    opacity: open ? 1 : 0,
                    visibility: open ? "visible" : "hidden",
                    pointerEvents: open ? "auto" : "none",
                }}
            >
                <Clear color="rgba(35, 39, 41, 1)" style={{ marginTop: 4 }} />
            </Button>
        </div>,
        document.body,
    );
}
